import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  padding: ${Spacing.xl};
  text-align: center;
`;

const ErrorIcon = styled.div`
  color: ${ThemeColors.error.main};
  margin-bottom: ${Spacing.lg};
  
  svg {
    width: 64px;
    height: 64px;
  }
`;

const ErrorTitle = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const ErrorMessage = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.xl};
  max-width: 500px;
`;

const RetryButton = styled.button`
  background-color: ${ThemeColors.primary.main};
  color: ${ThemeColors.primary.contrastText};
  border: none;
  padding: ${Spacing.md} ${Spacing.lg};
  border-radius: 8px;
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${ThemeColors.primary.dark};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

class ErrorBoundaryClass extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Unexpected error caught by ErrorBoundary:', error, errorInfo);
    
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorContainer>
          <ErrorIcon>
            <AlertTriangle />
          </ErrorIcon>
          <ErrorTitle>Something went wrong</ErrorTitle>
          <ErrorMessage>
            We're sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.
          </ErrorMessage>
          <RetryButton onClick={this.handleRetry}>
            <RefreshCw />
            Try Again
          </RetryButton>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  
  return (
    <ErrorBoundaryClass 
      {...props}
      fallback={
        <ErrorContainer>
          <ErrorIcon>
            <AlertTriangle />
          </ErrorIcon>
          <ErrorTitle>{t('common.error', 'Something went wrong')}</ErrorTitle>
          <ErrorMessage>
            {t('common.errorMessage', 'We\'re sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.')}
          </ErrorMessage>
          <RetryButton onClick={() => window.location.reload()}>
            <RefreshCw />
            {t('common.retry', 'Try Again')}
          </RetryButton>
        </ErrorContainer>
      }
    />
  );
};
